import type {
    CreateExtensionArgument,
    Extension,
    ExtensionAPI,
    InitializeExtArgs,
    CreateExtArgs
} from '@wix/document-manager-core'
import type {FileMediaType, SyncImportFileResponse} from '@wix/document-services-types'
import type {DistributorExtensionAPI} from '../distributor/distributor'
import type {EventHandlerExtApi, Handler} from './eventHandlerExtension'
import {aiFilesUploadServerlessUrl, aiFilesUploadSyncServerlessUrl, aiFileUploadServerlessUrl} from './constants'
import {postForMedia} from './utils'

export interface UploadFileResponse {
    executionId: string
    fileId: string
}

export interface UploadFilesSyncObject {
    id: string
    url: string
    mediaType: FileMediaType
    displayName?: string
}

export interface FileUploadApi {
    uploadFile(
        handler: (fileId: string) => Handler,
        url: string,
        caller?: string,
        timeOutInMS?: number
    ): Promise<boolean>
    uploadFiles(
        handlers: ((fileId: string) => Handler)[],
        urls: string[],
        caller?: string,
        timeOutInMS?: number
    ): Promise<boolean>
    uploadFilesSync(files: UploadFilesSyncObject[]): Promise<{id: string; response: SyncImportFileResponse | null}[]>
}

export type FileUploadExtApi = ExtensionAPI & {fileUpload: FileUploadApi}

const fileUploadTimeout = 30000

const createExtension = ({environmentContext}: CreateExtensionArgument): Extension => {
    const initialize = async (extArgs: InitializeExtArgs) => {
        const {extensionAPI} = extArgs
        const {distributor} = extensionAPI as DistributorExtensionAPI

        // Register to distributor event for file uploads
        distributor.subscribeToMessage('FILE_UPLOAD', (data: any) =>
            (extensionAPI as EventHandlerExtApi).eventHandler.invokeExecutionHandler(data)
        )
    }

    const createExtensionAPI = ({extensionAPI}: CreateExtArgs): FileUploadExtApi => {
        const {serverFacade} = environmentContext
        const eventHandlerApi = (extensionAPI as EventHandlerExtApi).eventHandler

        const fetchFile = async (url: string): Promise<UploadFileResponse> => {
            return await postForMedia<UploadFileResponse>(serverFacade, aiFileUploadServerlessUrl, {
                url,
                // TODO: remove this once the serverless function is updated
                shouldReturnFileId: true
            })
        }

        const fetchFiles = async (urls: string[]): Promise<UploadFileResponse[]> => {
            return await postForMedia<UploadFileResponse[]>(serverFacade, aiFilesUploadServerlessUrl, {
                urls,
                // TODO: remove this once the serverless function is updated
                shouldReturnFileId: true
            })
        }

        const fetchFilesSync = async (
            urlsWithMetaData: UploadFilesSyncObject[]
        ): Promise<{id: string; response: SyncImportFileResponse | null}[]> => {
            return await postForMedia<{id: string; response: SyncImportFileResponse | null}[]>(
                serverFacade,
                aiFilesUploadSyncServerlessUrl,
                {
                    urlsWithMetaData
                }
            )
        }

        const uploadFile = async (
            handler: (fileId: string) => Handler,
            url: string,
            caller?: string,
            timeOutInMS: number = fileUploadTimeout
        ): Promise<boolean> => {
            const {executionId, fileId} = await fetchFile(url)
            eventHandlerApi.registerExecutionIdToHandler(executionId, handler(fileId), caller, timeOutInMS)
            return true
        }

        const uploadFiles = async (
            handlers: ((fileId: string) => Handler)[],
            urls: string[],
            caller?: string,
            timeOutInMS: number = fileUploadTimeout
        ): Promise<boolean> => {
            const fileResponses = await fetchFiles(urls)
            fileResponses.forEach((fileResponse, index) => {
                const {executionId, fileId} = fileResponse
                eventHandlerApi.registerExecutionIdToHandler(executionId, handlers[index](fileId), caller, timeOutInMS)
            })
            return true
        }

        const uploadFilesSync = (
            urlsWithMetaData: UploadFilesSyncObject[]
        ): Promise<{id: string; response: SyncImportFileResponse | null}[]> => {
            return fetchFilesSync(urlsWithMetaData)
        }

        return {
            fileUpload: {
                uploadFile,
                uploadFiles,
                uploadFilesSync
            }
        }
    }

    return {
        name: 'fileUpload',
        createExtensionAPI,
        initialize
    }
}

export {createExtension}
